import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { usePlayControllerVoiceV1, usePlayControllerMarkVoiceSeenV1 } from '@kidshome/api-contract';
import { cn } from '@kidshome/ui';
import { useFeature } from '@/entities/session';
import { Feature } from '@/shared/lib/features';
import { coinGlyphText } from '@/shared/ui';
import { renderVoiceLine, idleGreeting } from '@/widgets/narrator/render-voice';

/**
 * NarratorBubble (ADR-0095 §4) — the hero's inline "says" bubble. It renders the latest FRESH voice
 * reaction from the get-voice feed (`GET /v1/play/voice`) in the chosen archetype's voice (a server-
 * resolved i18n key + inert params; the client never re-derives meaning), and — when shown — marks the
 * feed seen up to that reaction (`POST /v1/play/voice/seen`). When nothing is fresh it shows a calm,
 * kind greeting (never absence-triggered, never guilt/FOMO copy).
 *
 * Speech-bubble framing: a small tail points to the hero. Reduced-motion-safe (no animation). The hero
 * is the speaker — the bubble is labelled as such for assistive tech. A caller may pass an explicit
 * `line` (already-resolved copy) to override the live feed (e.g. an evolution celebration).
 *
 * `archetypeId` is the viewer's active hero, so the calm idle fallback speaks in THAT hero's authored
 * voice (a prince sounds masculine, a princess feminine) instead of the generic, feminine-only
 * `hero.narrator.greeting`. Omit / pass null for a degraded or not-yet-loaded hero → the gender-safe
 * neutral pack.
 */
export function NarratorBubble({
  line,
  archetypeId = null,
  className,
}: {
  /** An already-resolved line to show; when omitted, the live feed (or a calm greeting) renders. */
  line?: string;
  /** The viewer's active hero archetype, so the idle fallback matches their hero's voice + gender. */
  archetypeId?: string | null;
  className?: string;
}): React.ReactElement {
  const { t } = useTranslation('play');
  const hasFeature = useFeature();
  const playEnabled = hasFeature(Feature.Play);

  const voice = usePlayControllerVoiceV1(undefined, {
    query: { enabled: playEnabled && line === undefined },
  });
  const markSeen = usePlayControllerMarkVoiceSeenV1();

  const latest = voice.data?.latest ?? null;
  const liveLine = latest ? renderVoiceLine(t, latest) : null;
  const text = line ?? liveLine ?? idleGreeting(t, archetypeId);

  // When a fresh reaction is shown, mark the feed seen up to it (idempotent; offline replays re-send
  // safely). Guarded so we only fire once per reaction id.
  const seenRef = useRef<string | null>(null);
  useEffect(() => {
    if (line !== undefined) return;
    if (latest && !latest.seen && seenRef.current !== latest.reactionId) {
      seenRef.current = latest.reactionId;
      markSeen.mutate({ data: { upToReactionId: latest.reactionId } });
    }
  }, [latest, line, markSeen]);

  return (
    <div
      className={cn(
        'relative rounded-card border border-border bg-surface px-4 py-3 text-sm font-medium text-text',
        // The speech-tail, anchored to the inline-start edge (logical, RTL-safe).
        'before:absolute before:-top-1.5 before:start-5 before:size-3 before:rotate-45 before:border-s before:border-t before:border-border before:bg-surface',
        className,
      )}
      role="note"
      aria-label={t('hero.narrator.label')}
    >
      {coinGlyphText(text)}
    </div>
  );
}
